import { useEffect, useRef } from "react";
import type { PanelType } from "../components/reusable/PanelSelector";
import { tryConsumeHandoffForPanel } from "../utils/svgToolHandoff";
import { isValidSvg } from "../utils/svgUtils";

export type SvgHandoffReceiverHandlers = {
    onPath?: (svg: { name: string; content: string }) => void;
    onObject?: (svg: { name: string; content: string }) => void;
    onPrimary?: (svg: { name: string; content: string }) => void;
};

/**
 * Consumes `?handoff=` once when this panel's provider mounts (declare after
 * `useToolParamPersistence` so preset hydration runs first).
 */
export function useSvgToolHandoffReceiver(
    panelId: PanelType,
    handlers: SvgHandoffReceiverHandlers,
    options?: { setError?: (message: string | null) => void },
): void {
    const handlersRef = useRef(handlers);
    handlersRef.current = handlers;
    const setErrorRef = useRef(options?.setError);
    setErrorRef.current = options?.setError;

    useEffect(() => {
        const payload = tryConsumeHandoffForPanel(panelId);
        if (!payload) return;

        if (!isValidSvg(payload.content)) {
            setErrorRef.current?.("Received SVG handoff was not valid SVG.");
            return;
        }

        const svg = { name: payload.name, content: payload.content };
        const h = handlersRef.current;
        if (payload.slot === "path") h.onPath?.(svg);
        else if (payload.slot === "object") h.onObject?.(svg);
        else h.onPrimary?.(svg);
    }, [panelId]);
}
